import { useCallback } from "react"
import { useAppDispatch, useAppStore } from "hooks/redux"
import { Creators as DataCreators } from "model/data"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { useCreateLayout, LAYOUTS_RESOURCE } from "resources/layouts"
import { useReseedInstance, DASHBOARD_INSTANCES_RESOURCE } from "resources/dashboard-instances"
import { instancesSlice } from "../store/instances-slice"
import { exportLayoutConfig, type DashboardPanelSlicesState } from "../layouts/export-layout-config"
import type { GetArrangement } from "./use-workspace-sync"

export interface SaveAsNewLayout {
  // Resolves on success (toasts); rejects on failure so SaveAsNewDialog's catch can keep the dialog
  // open with its inline retry message instead of silently closing on a swallowed error. [APP-3309]
  saveAsNew: (name: string) => Promise<void>
}

/**
 * "Save as new layout" (APP-2708).
 *
 * Snapshots the live dashboard state into a brand-new user-owned layout and re-points the current
 * instance at it (so subsequent shared-layout saves target the copy, not the original). The
 * originating layout is never written — this is a copy-out, not a save-back.
 *
 * @param dashboardId - The dashboard slot whose live state is exported; equals the instance to re-point.
 * @param getArrangement - Reads the live Dockview blob at call time (WYSIWYG); null when no engine is mounted.
 */
export function useSaveAsNewLayout(
  dashboardId: string,
  getArrangement: GetArrangement,
): SaveAsNewLayout {
  const store = useAppStore()
  const dispatch = useAppDispatch()
  const createLayout = useCreateLayout()
  const reseedInstance = useReseedInstance()

  const saveAsNew = useCallback(
    async (name: string) => {
      // A failure here propagates: SaveAsNewDialog's own catch surfaces it inline and keeps the
      // dialog open for retry, so this doesn't also toast (that would just duplicate the message). [APP-3309]
      // Export live state at call time (not a render-closure snapshot) so the copy is WYSIWYG.
      const state = store.getState() as unknown as DashboardPanelSlicesState
      const { config } = exportLayoutConfig(state, dashboardId, getArrangement())

      const layout = await createLayout(name, config)
      // Mirror the server's echoed row (it stamps layoutVersionSeeded) rather than guessing the version.
      const instance = await reseedInstance(dashboardId, layout.id)
      dispatch(instancesSlice.actions.upsertInstance({
        instanceId: instance.instanceId,
        name: instance.name,
        layoutId: instance.layoutId,
        layoutVersionSeeded: instance.layoutVersionSeeded,
        lastOpenedAt: instance.lastOpenedAt,
      }))
      // Reset both caches (mirrors useCreateDashboard): the instances list, since a later tab switch
      // re-hydrates from it and would reseed the old layout; and the layouts list. [APP-3266/3271]
      dispatch(DataCreators.resetCollections([DASHBOARD_INSTANCES_RESOURCE, LAYOUTS_RESOURCE]))

      dispatch(NotificationCreators.add("success", null, "Saved as new layout."))
    },
    [store, dispatch, createLayout, reseedInstance, dashboardId, getArrangement],
  )

  return { saveAsNew }
}
